// src/components/SalaryDownload.js
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import "./SalaryEmployee.css";

const SalaryDownload = () => {
  const [periods, setPeriods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState("");
  const [loading, setLoading] = useState(true);
  const [fetching, setFetching] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const navigate = useNavigate();

  // 期間一覧を取得（認証ユーザー本人のみ、URLにIDを含めない）
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await api.get("/salary/periods/", {
          params: { _t: Date.now() },
          headers: { "Cache-Control": "no-cache, no-store", Pragma: "no-cache" },
        });
        const list = Array.isArray(res.data) ? res.data : [];
        setPeriods(list);
        if (list.length > 0) {
          setSelectedPeriod(list[0].period || list[0].id);
        }
      } catch (e) {
        console.error("期間一覧の取得に失敗しました。", e);
        setPeriods([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // ダウンロード・表示共通のファイル取得関数
  const fetchSalaryFile = useCallback(async () => {
    if (!selectedPeriod) return null;
    setFetching(true);
    try {
      const res = await api.get(`/salary/${selectedPeriod}/download/`, {
        responseType: "blob",
        params: { _t: Date.now() },
        headers: { "Cache-Control": "no-cache, no-store", Pragma: "no-cache" },
      });
      return res.data;
    } catch (err) {
      console.error("給与明細取得失敗:", err);
      alert("この期間の給与明細が見つかりません。");
      return null;
    } finally {
      setFetching(false);
    }
  }, [selectedPeriod]);

  const handleDownload = async () => {
    const blob = await fetchSalaryFile();
    if (!blob) return;
    const url = window.URL.createObjectURL(new Blob([blob]));
    const a = document.createElement("a");
    a.href = url;
    a.download = `${selectedPeriod}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const handlePreview = async () => {
    // 既存のプレビューURLを解放
    if (previewUrl) {
      window.URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    const blob = await fetchSalaryFile();
    if (!blob) return;
    const url = window.URL.createObjectURL(
      new Blob([blob], { type: "application/pdf" })
    );
    // iOS Safariはasyncのあとにwindow.openをブロックするため、
    // アンカーリンクをレンダリングしてユーザーが直接タップできるようにする
    setPreviewUrl(url);
  };

  // 期間が変わったらプレビューをクリア
  const handlePeriodChange = (e) => {
    if (previewUrl) {
      window.URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    setSelectedPeriod(e.target.value);
  };

  if (loading) {
    return (
      <div className="salary-emp-page">
        <p className="salary-emp-loading">読み込み中...</p>
      </div>
    );
  }

  return (
    <div className="salary-emp-page">
      <h2 className="salary-emp-title">給与明細</h2>

      {periods.length === 0 ? (
        <p className="salary-emp-empty">給与明細はまだ登録されていません。</p>
      ) : (
        <>
          <div className="salary-emp-period-row">
            <label htmlFor="salary-period-select" className="salary-emp-period-label">
              期間
            </label>
            <select
              id="salary-period-select"
              className="salary-emp-select"
              value={selectedPeriod}
              onChange={handlePeriodChange}
            >
              {periods.map((item) => (
                <option key={item.id || item.period} value={item.period}>
                  {item.period.slice(0, 4)}年{item.period.slice(4, 6)}月
                </option>
              ))}
            </select>
          </div>

          <div className="salary-emp-btn-row">
            <button
              type="button"
              className="salary-emp-btn salary-emp-btn-primary"
              onClick={handlePreview}
              disabled={!selectedPeriod || fetching}
            >
              {fetching ? "取得中..." : "表示"}
            </button>
            <button
              type="button"
              className="salary-emp-btn salary-emp-btn-secondary"
              onClick={handleDownload}
              disabled={!selectedPeriod || fetching}
            >
              {fetching ? "取得中..." : "ダウンロード"}
            </button>
          </div>

          {/* スマートフォン対応: 非同期後のwindow.openはiOS Safariでブロックされるため
              アンカーリンクとしてレンダリングし、ユーザーが直接タップして開けるようにする */}
          {previewUrl && (
            <div className="salary-emp-preview-link-wrap">
              <a
                href={previewUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="salary-emp-btn salary-emp-btn-open"
              >
                📄 PDFを開く（タップしてください）
              </a>
            </div>
          )}
        </>
      )}

      <div className="salary-emp-back-wrap">
        <button
          type="button"
          className="salary-emp-btn salary-emp-btn-back"
          onClick={() => navigate("/card_front")}
        >
          戻る
        </button>
      </div>
    </div>
  );
};

export default SalaryDownload;
